import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FileText, Users, Brain, BarChart3, LogOut, Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const Layout = ({ children, user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const navigation = [
    { name: 'Dashboard', href: '/app/dashboard', icon: LayoutDashboard },
    { name: 'Invoices', href: '/app/invoices', icon: FileText },
    { name: 'Clients', href: '/app/clients', icon: Users },
    { name: 'AI Assistant', href: '/app/ai-assistant', icon: Brain },
    { name: 'Reports', href: '/app/reports', icon: BarChart3 },
  ];

  const isActive = (href) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <div className="flex h-screen bg-white" data-testid="app-layout">
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 z-40">
        <img
          src="https://cdn.prod.website-files.com/686695909d1dd285d3ea5c78/68693907254e9c834a679cee_Ardito%20Advisors%20PNG%20small.png"
          alt="Ardito Advisors"
          className="h-8 w-auto"
        />
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 text-slate-900"
          aria-label="Toggle menu"
        >
          {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={closeMobileMenu}
        />
      )}

      {/* Sidebar */}
      <div className={`
        w-64 border-r border-slate-200 flex flex-col bg-white
        md:relative md:translate-x-0
        fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="p-6 border-b border-slate-200">
          <img
            src="https://cdn.prod.website-files.com/686695909d1dd285d3ea5c78/68693907254e9c834a679cee_Ardito%20Advisors%20PNG%20small.png"
            alt="Ardito Advisors"
            className="h-12 w-auto mb-2"
            data-testid="app-logo"
          />
          <p className="text-xs text-slate-500">Collections Manager</p>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto" data-testid="main-navigation">
          {navigation.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={closeMobileMenu}
                data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-none border transition-colors ${
                  active
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'border-slate-200 bg-transparent hover:border-slate-900 hover:text-slate-900'
                }`}
              >
                <Icon className="h-5 w-5" strokeWidth={1.5} />
                <span className="font-medium text-sm">{item.name}</span>
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-slate-200">
          <div className="mb-3">
            <p className="text-sm font-medium text-slate-900">{user?.full_name}</p>
            <p className="text-xs text-slate-500">{user?.company_name}</p>
          </div>
          <Button
            onClick={handleLogout}
            data-testid="logout-button"
            variant="outline"
            className="w-full justify-start gap-2 rounded-none border-slate-200 hover:border-slate-900"
          >
            <LogOut className="h-4 w-4" />
            <span>Logout</span>
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden md:ml-0 pt-16 md:pt-0">
        <div className="flex-1 overflow-auto">{children}</div>
      </div>
    </div>
  );
};

export default Layout;